import React, {Component, createRef, RefObject} from 'react';
import {animated, Transition} from 'react-spring';

import {getWindow, isHtmlElement} from '../../../../libs/shared/helpers/src/browser/domHelpers';
import {TaskSourcesEnum} from '../../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {styled} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {BannerDefinition} from './bannersContext';

/*
 * Constants.
 */

const BANNER_CLASSNAME = 'banner-wrapper';
const SLIDE_DURATION_MS = 380;
const INITIAL_RENDER_OFFSET = 44; // Distance the banner slides in from on initial mount

/*
 * Types.
 */

type BannerOffsets = Record<string, number>;
type BannerElements = Record<string, HTMLElement>;

interface BannerSnapshot {
  initialOffsets: BannerOffsets;
  initialElements: BannerElements;
}

/*
 * Props.
 */

interface BannerAnimationProps {
  width: number;
  banners: readonly BannerDefinition[];
}

/*
 * Styles.
 */

const StyledBannersDiv = styled.div`
  display: flex;
  flex-direction: column-reverse;
  pointer-events: none;
`;

const StyledBannerDiv = styled(animated.div)`
  padding-top: 8px;
  width: 400px;
  will-change: transform, opacity;
`;

/*
 * Component. BannerAnimation animates the Banner slide-in effect using FLIP
 * style animations.
 */
export class BannerAnimation extends Component<BannerAnimationProps> {
  private containerRef: RefObject<HTMLDivElement>;
  private animationFrameId: number;
  constructor(props: BannerAnimationProps) {
    super(props);

    this.containerRef = createRef();
    this.animationFrameId = 0;
  }

  findBanners() {
    const container = this.containerRef.current;
    if (!container) {
      return [];
    }
    return Array.from(container.getElementsByClassName(BANNER_CLASSNAME)).filter(isHtmlElement);
  }

  getSnapshotBeforeUpdate(): BannerSnapshot {
    // The getSnapshotBeforeUpdate lifecycle method is called before updates are committed
    // to the DOM. This allows us to capture the initial positions of the elements which
    // are already on the screen (the "F" in "FLIP")

    // Animations must be stopped so there's no movement between now and when our animation
    // starts playing after componentDidUpdate.
    this.stopAnimation();
    const banners = this.findBanners();
    const initialOffsets: BannerOffsets = {};
    const initialElements: BannerElements = {};
    for (const banner of banners) {
      initialOffsets[banner.id] = banner.getBoundingClientRect().top;
      initialElements[banner.id] = banner;
    }
    // React passes this returned value to componentDidUpdate's third param.
    return {
      initialOffsets,
      initialElements,
    };
  }

  componentDidUpdate(prevProps: BannerAnimationProps, prevState: {}, snapshot: BannerSnapshot) {
    // componentDidUpdate is called right after DOM mutations occur, so this is where we
    // calculate the final positions of all of the elements (the "L" in FLIP.)
    // NOTE: DOM reads and writes must be batched to avoid layout thrashing.
    // The snapshot param is the return value from getSnapshotBeforeUpdate.

    const finalOffsets: BannerOffsets = {};
    const banners = this.findBanners();

    // First check for elements which are about to be removed. These need to be removed from the
    // document flow so the positions can be calculated for the remaining elements. At the same
    // time they need to retain their current position so they can be faded out. This is accomplished
    // using fixed positioning.
    const currentBannerIds = new Set(this.props.banners.map((banner) => banner.id));
    for (const banner of prevProps.banners) {
      // This loop is only for DOM writes.
      if (currentBannerIds.has(banner.id)) {
        continue;
      }

      const id = makeBannerId(banner);
      const element = snapshot.initialElements[id];
      if (!element) {
        continue;
      }

      element.style.position = 'fixed';
      element.style.top = `${snapshot.initialOffsets[id]}px`;
      // pointerEvents none is to prevent fading banners from blocking clicks to banners behind them.
      element.style.pointerEvents = 'none';
    }

    // Once the removed elements have been taken care of, the element positionings can be read.
    const initialOffsets = {...snapshot.initialOffsets};
    for (const banner of banners) {
      // This loop is only for DOM reads.
      const finalOffset = banner.getBoundingClientRect().top;
      finalOffsets[banner.id] = finalOffset;

      // If there's no initial offset, this is a newly added banner. Manually set the initial
      // offset to slide the banner in.
      if (!initialOffsets[banner.id]) {
        initialOffsets[banner.id] = finalOffset + INITIAL_RENDER_OFFSET;
      }
    }

    this.animationFrameId = this.requestAnimationFrame((timestamp: number) =>
      this.playAnimation(timestamp, timestamp, banners, initialOffsets, finalOffsets),
    );
  }

  componentWillUnmount() {
    this.stopAnimation();
  }

  requestAnimationFrame(callback: FrameRequestCallback) {
    const container = this.containerRef.current;
    if (!container) {
      return 0;
    }

    const window = getWindow(container);

    // If window still isn't found, something is very wrong. Not having access
    // to window.requestAnimationFrame will prevent the banners from displaying,
    // so this needs to be reported.
    if (!window) {
      reportToBugsnag(new Error('Window element not found for BannerAnimation component.'));
      return 0;
    }

    return window.requestInstrumentedAnimationFrame(callback, {source: TaskSourcesEnum.COMPONENT_RENDER});
  }

  stopAnimation() {
    const windowElement = getWindow(this.containerRef.current);
    if (!windowElement) {
      return;
    }
    windowElement.cancelAnimationFrame(this.animationFrameId);
  }

  playAnimation(
    startTime: number,
    currentTime: number,
    banners: HTMLElement[],
    initialOffsets: BannerOffsets,
    finalOffsets: BannerOffsets,
  ) {
    // Once the initial and final offsets are calculated, this function handles the
    // actual animation loop. (This is the "IP" in FLIP.) Don't do any DOM reads in
    // here, or you're likely to cause layout thrashing.

    const proportionTimeElapsed = computeProportionTimeElapsed({
      startTime,
      currentTime,
      animationDuration: SLIDE_DURATION_MS,
    });

    const easedProportion = easeOutExpo(proportionTimeElapsed);
    for (const banner of banners) {
      const totalOffset = initialOffsets[banner.id] - finalOffsets[banner.id];
      const currentOffset = totalOffset - totalOffset * easedProportion;
      // Use translate3d for hardware acceleration
      banner.style.transform = `translate3d(0, ${currentOffset}px, 0)`;
    }

    if (proportionTimeElapsed === 1) {
      return;
    }

    this.animationFrameId = this.requestAnimationFrame((timestamp) =>
      this.playAnimation(startTime, timestamp, banners, initialOffsets, finalOffsets),
    );
  }

  render() {
    return (
      <StyledBannersDiv data-testid="banners-host" ref={this.containerRef}>
        {/* React Spring is used to handle the opacity transition and manage unmounted components. */}
        <Transition items={this.props.banners} from={{opacity: 0}} enter={{opacity: 1}} leave={{opacity: 0}}>
          {({opacity}, banner) => {
            const id = makeBannerId(banner);
            return (
              <StyledBannerDiv id={id} className={BANNER_CLASSNAME} key={id} style={{opacity}}>
                <div
                  style={{width: this.props.width}}
                  ref={(ref: any) => ref && ref.appendChild(banner.node)}
                />
              </StyledBannerDiv>
            );
          }}
        </Transition>
      </StyledBannersDiv>
    );
  }
}

/*
 * Helpers.
 */

interface ComputeProportionTimeElapsedParams {
  startTime: number;
  currentTime: number;
  animationDuration: number;
}

// Returns a value between 0 (no time elapsed) and 1 (duration met or exceeded)
function computeProportionTimeElapsed({
  startTime,
  currentTime,
  animationDuration,
}: ComputeProportionTimeElapsedParams) {
  return Math.min((currentTime - startTime) / animationDuration, 1);
}

// Generates string used for the banner's HTML id.
function makeBannerId(banner: BannerDefinition) {
  return `banner-${banner.id}`;
}

// Taken from easings.net
function easeOutExpo(x: number) {
  return x === 1 ? 1 : 1 - 2 ** (-10 * x);
}
